import { z } from 'zod';
/**
 * Compact replay envelope. Submitted by the client as part of
 * `POST /v1/cartridges/:slug/runs` (see `docs/spec-api.md` §5). Re-simulated
 * server-side by `packages/core/validators/<slug>.ts` against the same
 * cartridge code — if the simulated final score doesn't match the claimed
 * one, the run is flagged or rejected.
 *
 * **Frame compaction:** we don't ship one entry per simulated tick (that
 * would be 3600 entries for a 60-second run). Instead each `frame` entry is
 * `[tick, inputBitmask]` and represents a *change* — the cabinet records the
 * tick at which a button transitioned. The validator replays by holding the
 * last-seen bitmask between entries.
 *
 * **Size budget:** typical 60-second arcade run = ~3 KB. We reject anything
 * over 50000 ticks (~14 minutes at 60fps) at the API boundary regardless.
 */
export const ReplayFrameSchema = z.tuple([
    z.number().int().min(0), // tick number
    z.number().int().min(0).max(0xffff), // input bitmask + analog low bits
]);
/**
 * The full replay envelope clients post on run submission.
 */
export const ReplayEnvelopeSchema = z.object({
    /** PRNG seed string used for the run. Hex or alphanumeric, ≤ 64 chars. */
    seed: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[A-Za-z0-9_-]+$/, 'seed must be URL-safe'),
    /**
     * Compacted frame log. See file-level comment for compaction rules.
     * Bounded by `maxFrames` to prevent abuse via giant payloads.
     */
    frames: z.array(ReplayFrameSchema).min(1).max(50_000),
    /** Score the client claims for this run. Server re-simulates and compares. */
    finalScore: z.number().int().min(0),
    /**
     * Cartridge version the run was played against. Server uses this to
     * pin the validator version for re-simulation.
     */
    cartridgeVersion: z.number().int().min(1),
    /**
     * Total simulation ticks. Sanity-check value the validator uses to bound
     * its loop independent of the frame log (which only holds transitions).
     */
    durationTicks: z.number().int().min(1).max(50_000),
});
//# sourceMappingURL=replay.js.map